import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AdvWorkflow from '../AdvWorkflow/AdvWorkflowContainer';
import ProgressBar from '../ProgressBar/ProgressBarContainer';
import {
  isNonEmptyString,
  isNumeric,
  createCustomPropType,
  isAnything,
  isUuid,
} from '../../../js/app/react/validators';
import { ITERATOR_POST_END, ITERATOR_PRE_BEGIN, JUMP_TYPES } from './AdvWorkflowHelpers';
import AdvWorkflowSummary from './AdvWorkflowSummary/AdvWorkflowSummaryContainer';
import workflowAnalyticsEvents from '../AnalyticsWorkflow/AnalyticsWorkflow';
import XMobileNavigation from '../XMobileNavigation/XMobileNavigationContainer';

const STAGE_CLEAR = 0;
const STAGE_INIT = 1;
const STAGE_POPULATED = 2;
const STAGE_VISIBLE = 3;

export default class AdvWorkflowShellView extends Component {
  constructor() {
    super();
    this.state = {
      stage: STAGE_CLEAR,
      lastStepResponse: null,
      canGoToPlan: false,
    };
    this.resetStateMachine = this.resetStateMachine.bind(this);
  }
  componentDidMount() {
    const { resetWorkflow, selectedGoalId } = this.props;
    resetWorkflow(selectedGoalId);
  }

  UNSAFE_componentWillReceiveProps({ currentClientId, clientId, isPopulated, isAnyStepVisible }) {
    switch (this.state.stage) {
      case STAGE_CLEAR: {
        if (isNumeric(currentClientId) && currentClientId === clientId) {
          this.setState({
            stage: STAGE_INIT,
          });
        }
        break;
      }
      case STAGE_INIT: {
        if (isPopulated) {
          this.setState({
            stage: STAGE_POPULATED,
          });
        }
        break;
      }
      case STAGE_POPULATED: {
        if (isAnyStepVisible) {
          this.setState({
            stage: STAGE_VISIBLE,
          });
        }
        break;
      }
      case STAGE_VISIBLE: {
        if (!isAnyStepVisible) {
          this.setState({
            stage: STAGE_POPULATED,
          });
        }
        break;
      }
      default:
    }
  }

  componentDidUpdate({
    currentClientId: previousCurrentClientId,
    isPopulated: previousIsPopulated,
    iterator: previousIterator,
    isInSummaryEdit: previousIsInSummaryEdit,
    scrollMobileToTop,
  }) {
    const {
      clientId,
      currentClientId,
      fetchData,
      workflowId,
      isPopulated,
      isAnyStepVisible,
      advanceToFirstStep,
      advanceToLastStep,
      advanceToSummary,
      goToSummaryWhenReady,
      goToEnd,
      initializeSection,
      iterator,
      saveAndQueryForNextAction,
      advanceToLastViewedStep,
      hasAutoOutput,
      outputFunction,
      canGoBack,
      jumpType,
      isInSummaryEdit,
      onLastStepResponseListener,
    } = this.props;

    if (!isNumeric(currentClientId)) {
      initializeSection(workflowId, clientId);
      return;
    }

    if (currentClientId !== clientId) {
      this.resetStateMachine();
      return;
    }

    if (!isNumeric(previousCurrentClientId)) {
      fetchData(workflowId, currentClientId);
      return;
    }

    if (!isPopulated) {
      return;
    }

    if (!previousIsPopulated && !isAnyStepVisible) {
      if (goToSummaryWhenReady === '1') {
        advanceToSummary(workflowId, currentClientId);
      } else if (goToEnd) {
        advanceToLastStep(workflowId, currentClientId);
      } else {
        advanceToFirstStep(workflowId, currentClientId);
      }
      return;
    }

    if (iterator === previousIterator) {
      return;
    }

    if (typeof scrollMobileToTop === 'function') {
      scrollMobileToTop();
    }

    const hadStack = !isInSummaryEdit && previousIsInSummaryEdit;

    if (iterator === ITERATOR_POST_END) {
      // Only save if we arrived to the Summary by going through the workflow
      // otherwise just fire the analytics event
      if (!hasAutoOutput) {
        workflowAnalyticsEvents(workflowId, 'summary_visited');
      }
      if (
        (previousIterator !== ITERATOR_PRE_BEGIN || jumpType === JUMP_TYPES.RANDOM_ACCESS) &&
        !hadStack
      ) {
        saveAndQueryForNextAction(
          workflowId,
          clientId,
          lastStepResponse => {
            onLastStepResponseListener(lastStepResponse);
            this.setState({
              lastStepResponse,
            });
          },
          (canGoToPlan, lastStepResponse) => {
            this.setState({ canGoToPlan });
            if (hasAutoOutput) {
              outputFunction(lastStepResponse, canGoToPlan);
            }
          },
          (canGoToAccounts, lastStepResponse) => {
            if (hasAutoOutput) {
              outputFunction(lastStepResponse, canGoToAccounts);
            }
          },
        );
      }
    } else if (iterator === ITERATOR_PRE_BEGIN) {
      if (canGoBack) {
        window.history.back();
      } else {
        advanceToLastViewedStep(workflowId, currentClientId);
      }
    }
  }

  resetStateMachine() {
    const { resetWorkflow, selectedGoalId } = this.props;
    this.setState({
      stage: STAGE_CLEAR,
    });
    resetWorkflow(selectedGoalId);
  }

  render() {
    const {
      workflowId,
      steps,
      isInSummary,
      onFinish,
      goToSummaryWhenReady,
      outputFunction,
    } = this.props;
    const { stage, canGoToPlan, lastStepResponse } = this.state;

    return (
      <div>
        {stage !== STAGE_CLEAR && (
          <section
            className={`adv-workflow-container-ie11${
              isInSummary ? ' adv-workflow-section-hidden' : ''
            }`}
          >
            <div className="adv-workflow-container-outer">
              {stage === STAGE_VISIBLE ? <ProgressBar workflowId={workflowId} /> : null}
              <XMobileNavigation workflowId={workflowId} />
              <div className="adv-workflow-container-inner">
                <div className="adv-workflow-content generic-workflow">
                  <AdvWorkflow workflowId={workflowId} branch={[]} steps={steps} />
                </div>
              </div>
            </div>
          </section>
        )}
        <AdvWorkflowSummary
          workflowId={workflowId}
          goToSummaryWhenReady={goToSummaryWhenReady}
          onFinish={onFinish}
          canGoToPlan={canGoToPlan}
          lastStepResponse={lastStepResponse}
          outputFunction={outputFunction}
          stageVisible={stage === STAGE_VISIBLE}
        />
      </div>
    );
  }
}

AdvWorkflowShellView.defaultProps = {
  currentClientId: null,
  outputFunction: () => {},
  hasAutoOutput: false,
  onLastStepResponseListener: () => {},
  selectedGoalId: null,
};

AdvWorkflowShellView.propTypes = {
  goToSummaryWhenReady: PropTypes.string.isRequired,
  advanceToFirstStep: PropTypes.func.isRequired,
  isPopulated: PropTypes.bool.isRequired,
  isAnyStepVisible: PropTypes.bool.isRequired,
  initializeSection: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  workflowId: createCustomPropType(isNonEmptyString).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  currentClientId: createCustomPropType(isAnything),
  workflowSubdivision: PropTypes.shape({}).isRequired,
  resetWorkflow: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(PropTypes.func).isRequired,
  analyticsWorkflowStarted: PropTypes.string.isRequired,
  iterator: PropTypes.shape({}).isRequired,
  outputFunction: PropTypes.func,
  isInSummary: PropTypes.bool.isRequired,
  advanceToSummary: PropTypes.func.isRequired,
  saveAndQueryForNextAction: PropTypes.func.isRequired,
  advanceToLastViewedStep: PropTypes.func.isRequired,
  hasAutoOutput: PropTypes.bool,
  canGoBack: PropTypes.bool.isRequired,
  jumpType: PropTypes.number.isRequired,
  onFinish: PropTypes.func.isRequired,
  advanceToLastStep: PropTypes.func.isRequired,
  goToEnd: PropTypes.bool.isRequired,
  isInSummaryEdit: PropTypes.bool.isRequired,
  onLastStepResponseListener: PropTypes.func,
  selectedGoalId: createCustomPropType(isUuid),
};
